import { isTenantTypeAllowed, TENANT_TYPES } from "utils/tenantAccess";

// Tabs that depend on HealthLink and should be hidden on the consult
// (editable) view when practiceSettings.healthLink is off. The read-only
// view still shows them so historical rows remain viewable.
const HEALTHLINK_HIDDEN_TABS = [
  "add-vaccine",
  "add-referral",
  "add-lab-order",
  "add-form",
];

// Tabs that can be switched off individually in Practice Settings. Same
// reasoning as HealthLink above: hidden on the live consult when off, but
// still shown on the read-only view so historical rows remain viewable.
const SETTINGS_GATED_TABS = [
  { value: "send-email", settingKey: "sendEmail" },
  { value: "add-sick-cert", settingKey: "sickCert" },
  { value: "add-letter", settingKey: "letter" },
];

// A consultant practice has no HealthLink at all, so the four HealthLink
// actions are hidden for it on both the live and the read-only consult —
// unlike the healthLink practice setting above, which keeps them on the
// read-only view (KIN-2121). `tenantTypes` follows the NavigationBar /
// menuConfig convention: omitted = visible to every tenant type, and an
// unknown tenant type fails open.
const getAllTabs = ({ isReadOnly, hasEmail }) => [
  { value: "add-note", label: isReadOnly ? "Notes" : "Add Note" },
  {
    value: "add-prescription",
    label: isReadOnly ? "Prescription" : "Add Prescription",
  },
  // Vaccines carry no consultationId, so there is nothing historical to show once
  // a consultation is locked — the tab only makes sense on the live consult.
  ...(!isReadOnly
    ? [
        {
          value: "add-vaccine",
          label: "Add Vaccine",
          tenantTypes: [TENANT_TYPES.GP],
        },
      ]
    : []),
  // Same reasoning as Add Vaccine: no historical per-consultation baseline
  // view exists yet, so this only makes sense on the live consult.
  ...(!isReadOnly ? [{ value: "add-baseline", label: "Add Baseline" }] : []),
  {
    value: "add-referral",
    label: isReadOnly ? "Referrals" : "Add Referral",
    tenantTypes: [TENANT_TYPES.GP],
  },
  {
    value: "add-lab-order",
    label: isReadOnly ? "Add Lab Orders" : "Add Lab Order",
    tenantTypes: [TENANT_TYPES.GP],
  },
  { value: "add-letter", label: isReadOnly ? "Letters" : "Add Letter" },
  {
    value: "add-sick-cert",
    label: isReadOnly ? "Sick Certs" : "Add Sick Cert",
  },
  {
    value: "add-form",
    label: isReadOnly ? "Forms" : "Add Form",
    tenantTypes: [TENANT_TYPES.GP],
  },
  ...(hasEmail
    ? [{ value: "send-email", label: isReadOnly ? "Emails" : "Send Email" }]
    : []),
];

export const getConsultationTabOptions = ({
  isReadOnly = false,
  hasEmail = false,
  practiceSettings,
  tenantType,
} = {}) => {
  const allTabs = getAllTabs({ isReadOnly, hasEmail }).filter((tab) =>
    isTenantTypeAllowed(tab.tenantTypes, tenantType)
  );

  if (isReadOnly) return allTabs;

  const hideHealthlinkTabs = practiceSettings?.healthLink === false;
  const hiddenSettingsTabs = SETTINGS_GATED_TABS.filter(
    ({ settingKey }) => practiceSettings?.[settingKey] === false
  ).map(({ value }) => value);

  return allTabs.filter(
    (tab) =>
      !(hideHealthlinkTabs && HEALTHLINK_HIDDEN_TABS.includes(tab.value)) &&
      !hiddenSettingsTabs.includes(tab.value)
  );
};
